{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "Palette",
  "description": "Semantic palette measured on docs/reference/cornelis-anthonisz-1538.jpg (2000 x 1454, sRGB) inside the hand-drawn class regions of tools/painting/regions.json. Every colour is given as an 8-bit sRGB hex string and as linear-light RGB floats (IEC 61966-2-1 decoding of the hex value, 4 decimals).",
  "type": "object",
  "additionalProperties": false,
  "required": ["version", "source", "method", "classes", "global"],
  "properties": {
    "version": {"description": "Schema version.", "const": 1},
    "source": {
      "title": "PaletteSource",
      "type": "object",
      "additionalProperties": false,
      "required": ["image", "width", "height", "colorSpace", "lineDetector"],
      "properties": {
        "image": {"type": "string", "description": "Project-relative path of the measured image."},
        "width": {"type": "integer", "description": "Pixels."},
        "height": {"type": "integer", "description": "Pixels."},
        "colorSpace": {"type": "string", "description": "Colour space of the pixel values."},
        "lineDetector": {
          "title": "LineDetector",
          "type": "object",
          "additionalProperties": false,
          "required": ["window", "threshold", "note"],
          "properties": {
            "window": {"type": "integer", "description": "Square closing/opening window of the top-hat transforms, pixels (2000 px frame)."},
            "threshold": {"type": "number", "description": "Minimum black (dark strokes) or white (highlights) top-hat response, CIE L* units."},
            "note": {"type": "string"}
          }
        }
      }
    },
    "method": {
      "title": "PaletteMethod",
      "description": "Definitions of the statistics, in words.",
      "type": "object",
      "additionalProperties": {"type": "string"}
    },
    "classes": {
      "title": "PaletteClasses",
      "type": "object",
      "required": ["skyZenith", "skyMid", "skyHorizon", "cloudDark", "farFields", "nearFields", "polderFields", "ditchLines", "dikeRoads", "ijWater", "canalWater", "riverWater", "roofTile", "roofDark", "roofSlate", "wallLight", "wallBrick", "streetGround", "cityWall", "towerStone", "treeFoliage", "gardenGrass", "shipHull", "sail", "flagRed", "lineworkDark", "highlightWhite", "gunSmoke"],
      "properties": {
        "skyZenith": {"$ref": "#/$defs/ClassStats"},
        "skyMid": {"$ref": "#/$defs/ClassStats"},
        "skyHorizon": {"$ref": "#/$defs/ClassStats"},
        "cloudDark": {"$ref": "#/$defs/ClassStats"},
        "farFields": {"$ref": "#/$defs/ClassStats"},
        "nearFields": {"$ref": "#/$defs/ClassStats"},
        "polderFields": {"$ref": "#/$defs/ClassStats"},
        "ditchLines": {"$ref": "#/$defs/ClassStats"},
        "dikeRoads": {"$ref": "#/$defs/ClassStats"},
        "ijWater": {"$ref": "#/$defs/ClassStats"},
        "canalWater": {"$ref": "#/$defs/ClassStats"},
        "riverWater": {"$ref": "#/$defs/ClassStats"},
        "roofTile": {"$ref": "#/$defs/ClassStats"},
        "roofDark": {"$ref": "#/$defs/ClassStats"},
        "roofSlate": {"$ref": "#/$defs/ClassStats"},
        "wallLight": {"$ref": "#/$defs/ClassStats"},
        "wallBrick": {"$ref": "#/$defs/ClassStats"},
        "streetGround": {"$ref": "#/$defs/ClassStats"},
        "cityWall": {"$ref": "#/$defs/ClassStats"},
        "towerStone": {"$ref": "#/$defs/ClassStats"},
        "treeFoliage": {"$ref": "#/$defs/ClassStats"},
        "gardenGrass": {"$ref": "#/$defs/ClassStats"},
        "shipHull": {"$ref": "#/$defs/ClassStats"},
        "sail": {"$ref": "#/$defs/ClassStats"},
        "flagRed": {"$ref": "#/$defs/ClassStats"},
        "lineworkDark": {"$ref": "#/$defs/ClassStats"},
        "highlightWhite": {"$ref": "#/$defs/ClassStats"},
        "gunSmoke": {"$ref": "#/$defs/ClassStats"}
      },
      "additionalProperties": {"$ref": "#/$defs/ClassStats"},
      "propertyNames": {"type": "string", "pattern": "^[a-z][A-Za-z0-9]*$"}
    },
    "global": {"$ref": "#/$defs/GlobalStats"}
  },
  "$defs": {
    "Vec3": {"type": "array", "items": {"type": "number"}, "minItems": 3, "maxItems": 3},
    "Color": {
      "title": "PaletteColor",
      "type": "object",
      "additionalProperties": false,
      "required": ["hex", "linear"],
      "properties": {
        "hex": {"type": "string", "pattern": "^#[0-9a-f]{6}$", "description": "8-bit sRGB, lower-case #rrggbb."},
        "linear": {"$ref": "#/$defs/Vec3", "description": "Linear-light RGB in [0, 1] (sRGB decoding of hex)."}
      }
    },
    "Centre": {
      "title": "PaletteCentre",
      "type": "object",
      "additionalProperties": false,
      "required": ["hex", "linear", "weight"],
      "properties": {
        "hex": {"type": "string", "pattern": "^#[0-9a-f]{6}$", "description": "8-bit sRGB, lower-case #rrggbb."},
        "linear": {"$ref": "#/$defs/Vec3", "description": "Linear-light RGB in [0, 1]."},
        "weight": {"type": "number", "minimum": 0, "maximum": 1, "description": "Share of the class pixels in this k-means cluster."}
      }
    },
    "LuminanceSpread": {
      "title": "LuminanceSpread",
      "type": "object",
      "additionalProperties": false,
      "required": ["p10", "p50", "p90"],
      "properties": {
        "p10": {"type": "number", "description": "10th percentile of relative luminance Y (linear, 0-1)."},
        "p50": {"type": "number", "description": "Median relative luminance Y."},
        "p90": {"type": "number", "description": "90th percentile of relative luminance Y."}
      }
    },
    "ClassStats": {
      "title": "ClassStats",
      "type": "object",
      "additionalProperties": false,
      "required": ["description", "filter", "polygonCount", "pixelCount", "median", "p10", "p90", "luminance", "contrastRatioP90P10", "meanSaturation", "centres"],
      "properties": {
        "description": {"type": "string"},
        "filter": {"enum": ["none", "excludeLines", "linesOnly", "highlightsOnly"], "description": "Stroke filter applied inside the regions: none, drop dark strokes, keep only dark strokes, keep only bright strokes."},
        "strokeThreshold": {"type": "number", "description": "Class-specific top-hat threshold replacing source.lineDetector.threshold for this class, CIE L* units."},
        "polygonCount": {"type": "integer", "description": "Hand-drawn polygons of the class."},
        "pixelCount": {"type": "integer", "description": "Measured pixels after filtering."},
        "median": {"$ref": "#/$defs/Color", "description": "Per-channel median."},
        "p10": {"$ref": "#/$defs/Color", "description": "Per-channel 10th percentile."},
        "p90": {"$ref": "#/$defs/Color", "description": "Per-channel 90th percentile."},
        "luminance": {"$ref": "#/$defs/LuminanceSpread"},
        "contrastRatioP90P10": {"type": "number", "description": "Luminance p90 / p10 (dimensionless)."},
        "meanSaturation": {"type": "number", "description": "Mean HSV saturation, 0-1."},
        "centres": {"type": "array", "minItems": 1, "maxItems": 5, "items": {"$ref": "#/$defs/Centre"}, "description": "CIELAB k-means centres, heaviest first."}
      }
    },
    "Percentiles": {
      "title": "Percentiles",
      "type": "object",
      "additionalProperties": false,
      "required": ["p1", "p5", "p10", "p25", "p50", "p75", "p90", "p95", "p99"],
      "properties": {
        "p1": {"type": "number"}, "p5": {"type": "number"}, "p10": {"type": "number"},
        "p25": {"type": "number"}, "p50": {"type": "number"}, "p75": {"type": "number"},
        "p90": {"type": "number"}, "p95": {"type": "number"}, "p99": {"type": "number"}
      }
    },
    "GlobalStats": {
      "title": "GlobalStats",
      "type": "object",
      "additionalProperties": false,
      "required": ["pixelCount", "meanColor", "luminance", "lightness", "lightnessHistogram", "meanSaturation", "meanChroma", "rmsContrast", "michelsonContrastP5P95"],
      "properties": {
        "pixelCount": {"type": "integer"},
        "meanColor": {"$ref": "#/$defs/Color", "description": "Mean in linear light, re-encoded to sRGB."},
        "luminance": {"$ref": "#/$defs/Percentiles", "description": "Relative luminance Y (linear, 0-1) percentiles."},
        "lightness": {"$ref": "#/$defs/Percentiles", "description": "CIE L* (0-100) percentiles."},
        "lightnessHistogram": {
          "title": "LightnessHistogram",
          "type": "object",
          "additionalProperties": false,
          "required": ["quantity", "range", "bins", "counts"],
          "properties": {
            "quantity": {"type": "string"},
            "range": {"type": "array", "items": {"type": "number"}, "minItems": 2, "maxItems": 2, "description": "CIE L* range covered by the bins."},
            "bins": {"type": "integer"},
            "counts": {"type": "array", "items": {"type": "integer"}, "description": "Pixels per bin."}
          }
        },
        "meanSaturation": {"type": "number", "description": "Mean HSV saturation, 0-1."},
        "meanChroma": {"type": "number", "description": "Mean CIELAB chroma C*ab."},
        "rmsContrast": {"type": "number", "description": "Standard deviation / mean of relative luminance."},
        "michelsonContrastP5P95": {"type": "number", "description": "(Y95 - Y5) / (Y95 + Y5)."}
      }
    }
  }
}
